import * as React from 'react';
import classnames from 'classnames';
import './index.scss';

export interface IResizableProps {
  readonly onResizeRef?: (el: HTMLDivElement) => void;
  /*
   * 内部 div 元素的引用
   */

  readonly width: number;

  /** The maximum width the panel can be resized to.
   *
   * @default 350
   */
  readonly maximumWidth?: number;

  /**
   * The minimum width the panel can be resized to.
   *
   * @default 150
   */
  readonly minimumWidth?: number;

  readonly height?: number;

  /** The maximum height the panel can be resized to.
   *
   * @default 500
   */
  readonly maximumHeight?: number;

  /**
   * The minimum height the panel can be resized to.
   *
   * @default 200
   */
  readonly minimumHeight?: number;

  /** The optional ID for the root element. */
  readonly id?: string;

  /** The resize direction
   * @default 'x'
   */
  readonly resizeAxis?: 'x' | 'y';

  readonly style?: React.CSSProperties;

  /**
   * Handler called when the width or height of the component has changed
   * through an explicit resize event (dragging the handle).
   */
  readonly onResize?: (newWidthOrHeight: number) => void;

  /**
   * Handler called when the resizable component has been
   * reset (ie restored to its original width by double clicking
   * on the resize handle).
   */
  readonly onReset?: () => void;

  readonly enableZoomout: boolean; // 是否允许缩小

  // 对于没有传初始宽/高或者为0的初始值，会在初始化的时候计算内部宽高并调用此方法
  readonly onInitResize?: (widthOrHeight: number) => void;

  // 手动控制初始化计算
  readonly init: boolean;
}

/**
 * Component abstracting a resizable panel.
 *
 * Note: this component is pure, consumers must subscribe to the
 * onResize and onReset event and update the width prop accordingly.
 */
export class Resizable extends React.Component<
  IResizableProps,
  {
    readonly isActive: boolean;
    readonly width: number;
    readonly height?: number;
  }
> {
  public static defaultProps: IResizableProps = {
    width: 0,
    maximumWidth: 350,
    minimumWidth: 200,
    height: 0,
    maximumHeight: 500,
    minimumHeight: 200,
    resizeAxis: 'x',
    enableZoomout: true,
    init: true,
  };

  private startWidth: number | null = null;
  private startX: number | null = null;

  private startHeight: number | null = null;
  private startY: number | null = null;

  private resizableRef = React.createRef<HTMLDivElement>();

  public constructor(props: IResizableProps) {
    super(props);
    this.state = {
      isActive: false,
      width: props.width,
      height: props.height,
    };
  }

  public componentWillReceiveProps(props: IResizableProps) {
    if (this.props.init === false && props.init === true) {
      let { width, height } = this.state;
      const { resizeAxis, width: propsWidth, height: propsHeight } = props;
      if (
        (!propsWidth && resizeAxis === 'x') ||
        (!propsHeight && resizeAxis === 'y')
      ) {
        const pos = this.resizableRef.current?.getBoundingClientRect() || {
          width: 0,
          height: 0,
        };
        width = pos.width;
        height = pos.height;
        if (this.props?.onInitResize) {
          this.props.onInitResize(resizeAxis === 'x' ? width : height);
        }
        this.setState({ ...this.state, width, height });
      }
    }
  }

  public render() {
    const { width, height } = this.state;
    const { resizeAxis } = this.props;
    let newWidth: number | string;
    let newHeight: number | string | undefined;
    if ((!width && resizeAxis === 'x') || (!height && resizeAxis === 'y')) {
      newWidth = this.getInitWidth();
      newHeight = this.getInitHeight();
    } else {
      newWidth = this.getCurrentWidth();
      newHeight = this.getCurrentHeight();
    }

    const style: React.CSSProperties =
      this.props.resizeAxis === 'x'
        ? {
            width: newWidth,
            maxWidth: this.props.maximumWidth,
            minWidth: this.props.minimumWidth,
            ...this.props.style,
          }
        : {
            height: newHeight,
            maxHeight: this.props.maximumHeight,
            minHeight: this.props.minimumHeight,
            ...this.props.style,
          };

    const classNames = classnames(
      'resize-handle',
      this.state.isActive ? 'active' : '',
      this.props.resizeAxis === 'y' ? 'axis-y' : ''
    );

    return (
      <div
        id={this.props.id}
        ref={this.resizableRef}
        className="resizable-component"
        style={style}
      >
        {this.props.children}
        <div
          onMouseDown={this.handleDragStart}
          onDoubleClick={this.handleDoubleClick}
          className={classNames}
        />
      </div>
    );
  }

  public getInitWidth() {
    return 'auto';
  }

  public getInitHeight() {
    return 'auto';
  }

  /**
   * Returns the current width as determined by props.
   *
   * This value will be constrained by the maximum and minimum
   * with props and might not be identical to that of props.width.
   */
  private getCurrentWidth() {
    return this.clampWidth(this.props.width);
  }

  private getCurrentHeight() {
    if (this.props.height === undefined) {
      return undefined;
    }
    return this.clampHeight(this.props.height);
  }

  /**
   * Constrains the provided width to lie within the minimum and
   * maximum widths as determined by props
   */
  private clampWidth(width: number) {
    return Math.max(
      this.props.minimumWidth!,
      Math.min(this.props.maximumWidth!, width)
    );
  }

  private clampHeight(height: number) {
    return Math.max(
      this.props.minimumHeight!,
      Math.min(this.props.maximumHeight!, height)
    );
  }

  /**
   * Handler for when the user presses the mouse button over the resize
   * handle.
   */
  private handleDragStart = (e: React.MouseEvent<any>) => {
    this.startX = e.clientX;
    this.startWidth = this.getCurrentWidth() || null;
    this.startY = e.clientY;
    this.startHeight = this.getCurrentHeight() || null;

    this.setState({ isActive: true });

    document.addEventListener('mousemove', this.handleDragMove);
    document.addEventListener('mouseup', this.handleDragStop);

    e.preventDefault();
  };

  /**
   * Handler for when the user moves the mouse while dragging
   */
  private handleDragMove = (e: MouseEvent) => {
    let newClamped = 0;
    if (
      this.props.resizeAxis === 'x' &&
      this.startWidth !== null &&
      this.startX !== null
    ) {
      const deltaX = e.clientX - this.startX;
      const newWidth = this.startWidth + deltaX;
      newClamped = this.clampWidth(newWidth);
    } else if (
      this.props.resizeAxis === 'y' &&
      this.startHeight !== null &&
      this.startY !== null
    ) {
      const deltaY = e.clientY - this.startY;
      const newHeight = this.startHeight - deltaY;
      newClamped = this.clampHeight(newHeight);
    }

    if (!this.props.enableZoomout) {
      if (
        this.props.resizeAxis === 'x' &&
        newClamped &&
        this.state.width > newClamped
      )
        return;
      if (
        this.props.resizeAxis === 'y' &&
        newClamped &&
        this.state?.height &&
        this.state.height > newClamped
      )
        return;
    }

    if (!newClamped) {
      return;
    }

    if (this.props.onResize) {
      this.props.onResize(newClamped);
    }

    e.preventDefault();
  };

  /**
   * Handler for when the user lets go of the mouse button during
   * a resize operation.
   */
  private handleDragStop = (e: MouseEvent) => {
    this.setState({ isActive: false });

    document.removeEventListener('mousemove', this.handleDragMove);
    document.removeEventListener('mouseup', this.handleDragStop);

    e.preventDefault();
  };

  /**
   * Handler for when the resize handle is double clicked.
   *
   * Resets the panel width to its default value and clears
   * any persisted value.
   */
  private handleDoubleClick = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
  };
}
